import { defaultUseQueryRefetchOptions } from '@/_shared/constants'
import { FunnelCongruenceTabs, SignupPrompt } from '@/components/FCA'
import { LoginFCA } from '@/components/LoginFCA'
import { Stack } from '@/components/custom-components'
import { Loader } from '@/components/custom-components/Loader'
import { Button } from '@/components/shadcn/button.tsx'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/shadcn/form.tsx'
import { Input } from '@/components/shadcn/input'
import { Progress } from '@/components/shadcn/progress'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { useElementSize } from '@/hooks/useElementSize'
import showToastNotification from '@/hooks/useShowToast'
import { trpc } from '@/utils/trpc'
import { zodResolver } from '@hookform/resolvers/zod'
import { CheckCircle, Image, InfoTriangle, Upload, XCircle } from '@mynaui/icons-react'
import { createFileRoute } from '@tanstack/react-router'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import Dropzone, { DropzoneState } from 'shadcn-dropzone'
import { useLocalStorage } from 'usehooks-ts'
import { z } from 'zod'

type SearchParams = {
  sideBarOpen?: boolean
}

type ScoreItem = {
  score: number
  description: string
}

type ScoreBreakdown = Record<string, ScoreItem>

type CoherenceEvaluation = {
  overall_score: {
    score: number
    grade: string
    emotional_label: string
    visceral_description: string
  }
  score_breakdown: ScoreBreakdown
}

type KeyOpportunity = {
  heading: string
  description: string
}

type EvaluationSummary = {
  score: number
  key_opportunities: KeyOpportunity[]
}

type Analysis = {
  overview: string
  whatsWorkingWell: string[] | string
  areasForImprovement: string[] | string
}

type FunnelCongruenceAnalysis = {
  coherence_evaluation: CoherenceEvaluation
  opportunity_assessment: EvaluationSummary
  analysis: Analysis
}

export type FunnelCongruenceAnalysisResponse = {
  ad_images_data: string[] | string | undefined
  landing_page_image_data: string
  analysis: FunnelCongruenceAnalysis
}

export const Route = createFileRoute('/funnel-analyzer/')({
  component: All,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const sideBarOpen = search?.sideBarOpen as boolean

    return {
      sideBarOpen,
    }
  },
})

function All() {
  const [squareRef, { width = 0 }] = useElementSize()
  const [showLoginDialog, setShowLoginDialog] = useState<boolean>(true)
  const [showSignupPrompt, setShowSignupPrompt] = useState<boolean>(false)
  const [user] = useLocalStorage<{ name: string; email: string } | null>('fca_user', null)

  const [uploadStep, setUploadStep] = useState<number>(1)
  const [landingPageImage, setLandingPageImage] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [adImageUrl, setAdImageUrl] = useState<string | undefined>(undefined)
  const [lastCapturedUrl, setLastCapturedUrl] = useState<string>('')
  const [funnelCongruenceAnalysis, setFunnelCongruenceAnalysis] = useState<FunnelCongruenceAnalysisResponse | null>(
    null,
  )

  // Add scroll event listener to show signup prompt
  useEffect(() => {
    if (funnelCongruenceAnalysis && uploadStep === 2) {
      const handleScroll = () => {
        const scrollPosition = window.scrollY
        const windowHeight = window.innerHeight
        const documentHeight = document.documentElement.scrollHeight
        const scrollPercentage = (scrollPosition / (documentHeight - windowHeight)) * 100

        if (scrollPercentage >= 25 && !showSignupPrompt) {
          setShowSignupPrompt(true)
        }
      }

      window.addEventListener('scroll', handleScroll)
      return () => window.removeEventListener('scroll', handleScroll)
    }
  }, [funnelCongruenceAnalysis, uploadStep, showSignupPrompt])

  const formSchema = z.object({
    target_audience: z.string().optional(),
    channel: z.string().optional(),
    offer: z.string().optional(),
    adImage: z.instanceof(File).optional(),
    landingPageUrl: z.string().url('Please enter a valid URL'),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      target_audience: '',
      channel: '',
      offer: '',
      adImage: undefined,
      landingPageUrl: '',
    },
  })

  const { data: userCanCreateFunnelAnalysis } = trpc.canCreateFunnelAnalysis.useQuery(
    {
      email: user?.email || '',
    },
    {
      enabled: !!user?.email,
      ...defaultUseQueryRefetchOptions,
    },
  )

  const { mutate: uploadLandingPageMutation } = trpc.uploadAsUrl.useMutation({
    onSuccess: (data) => {
      setLandingPageImage(data)
      setIsUploading(false)
    },
    onError: () => {
      showToastNotification('error', {
        message: 'Error',
        description: 'An error has occurred. Please try again.',
      })
      setIsUploading(false)
    },
  })

  const { mutate: createFunnelCongruenceAnalysis, isPending: isCreatingFunnelCongruenceAnalysis } =
    trpc.createFunnelCongruenceAnalysis.useMutation({
      onSuccess: (data) => {
        if (data && 'ad_images_data' in data && 'landing_page_image_data' in data) {
          setFunnelCongruenceAnalysis(data as FunnelCongruenceAnalysisResponse)
          showToastNotification('success', {
            message: 'Funnel congruence analysis generated successfully',
          })
          setTimeout(() => {
            setUploadStep(2)
          }, 2000)

          // Show signup prompt after 30 seconds
          setTimeout(() => {
            setShowSignupPrompt(true)
          }, 30000)
        }
      },
      onError: () => {
        showToastNotification('error', {
          message: 'Error',
          description: 'An error has occurred. Try again',
        })
      },
    })

  const handleSubmitFunnelCongruenceAnalysis = async (values: z.infer<typeof formSchema>) => {
    try {
      if (adImageUrl && landingPageImage) {
        createFunnelCongruenceAnalysis({
          target_audience: values.target_audience,
          channel: values.channel,
          offer: values.offer,
          images: [
            {
              url: adImageUrl,
              type: 'ad',
            },
            {
              url: landingPageImage,
              type: 'landing-page',
            },
          ],
          landingPageUrl: values.landingPageUrl,
          name: user?.name,
          email: user?.email,
        })
      }
    } catch (error) {
      console.error('Error', error)
    }
  }

  const handleSubmitUploadAdImage = async (values: z.infer<typeof formSchema>) => {
    setIsUploading(true)
    try {
      if (!values.adImage) {
        throw new Error('Ad image is required')
      }

      // Upload ad image via axios
      const formData = new FormData()
      formData.append('images', values.adImage)

      const imageUploadResponse = await axios.post(
        `${import.meta.env.VITE_API_URL}funnel-congruence/image-upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )

      // Store the ad image URL
      if (imageUploadResponse && imageUploadResponse.data.imageUrls?.[0]) {
        const uploadedUrl = imageUploadResponse.data.imageUrls[0]
        setAdImageUrl(uploadedUrl)

        // Always regenerate landing page when ad image changes
        uploadLandingPageMutation({
          landing_page_url: values.landingPageUrl,
        })
        setLastCapturedUrl(values.landingPageUrl)
      }
    } catch (e) {
      console.error('Error', e)
      setIsUploading(false)
    }
  }

  return (
    <>
      <LoginFCA
        open={showLoginDialog && !user}
        onOpenChange={() => setShowLoginDialog(false)}
        setShowSignupPrompt={setShowSignupPrompt}
      />
      <SignupPrompt
        open={showSignupPrompt || userCanCreateFunnelAnalysis === false}
        onOpenChange={setShowSignupPrompt}
      />
      <Stack ref={squareRef} className="gap-3 lg:gap-6 pb-5 px-8 py-5">
        <Stack className="gap-3 lg:gap-8">
          <Stack className="gap-3 lg:gap-6">
            <div>
              <a href={'https://creativeos.com'}>
                <img alt={'CreativeOS'} src={'/images/logo-full-dark_mode.png'} className={'h-7  dark:block hidden'} />
                <img alt={'CreativeOS'} src={'/images/logo-full-light_mode.png'} className={'h-7 dark:hidden block'} />
              </a>
            </div>
            <div className={'flex flex-col lg:flex-row justify-between gap-5 items-center'}>
              <div className={''}>
                <h1 className={'text-lg lg:text-2xl font-semibold'}>Upload Your Ad and Capture Your Landing Page.</h1>
                <p className={'text-lg lg:text-base text-thememutedforeground mt-2'}>
                  See how well your ad and landing page work together with a visual analysis.
                </p>
              </div>
            </div>
          </Stack>
        </Stack>
        <div className="flex flex-col gap-3">
          <Form {...form}>
            {uploadStep === 1 ? (
              <div>
                {isCreatingFunnelCongruenceAnalysis ? (
                  <div className="flex flex-col justify-center items-center w-full h-[60vh] min-h-[60vh] max-h-[60vh]">
                    <Loader />
                    <p className="text-sm text-themeforeground mt-3">Scoring Funnel Congruence...</p>
                  </div>
                ) : (
                  <form onSubmit={form.handleSubmit(handleSubmitUploadAdImage)}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <h3>Upload Ad Image</h3>
                        <div>
                          <UploadAdImage
                            onFileSelect={(file) => {
                              form.setValue('adImage', file)
                            }}
                            previewUrl={adImageUrl}
                            disabled={isUploading}
                            onRemove={() => {
                              setAdImageUrl(undefined)
                              form.setValue('adImage', undefined)
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        {isUploading ? (
                          <div className="flex flex-col justify-center items-center w-full h-[50svh] lg:h-[65svh] lg:min-h-[65svh] lg:max-h-[65svh]">
                            <Loader />
                            <p className="text-sm text-themeforeground mt-3">Generating Screenshot...</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <h3>Landing Page URL</h3>
                            <FormField
                              control={form.control}
                              name="landingPageUrl"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      className="shadow-none"
                                      placeholder="e.g, https://creativeos.com"
                                      {...field}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault()
                                        }
                                      }}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            {landingPageImage ? (
                              <div className="mt-4">
                                <ScrollArea className="h-[54svh]">
                                  <img
                                    src={landingPageImage}
                                    alt="Landing Page Preview"
                                    className="w-full object-contain bg-gray-50"
                                  />
                                </ScrollArea>
                              </div>
                            ) : (
                              <div className="flex flex-col justify-center items-center h-[30svh] lg:h-[54svh] rounded-lg border-2 border-dashed border-border">
                                <div>
                                  <span>
                                    <Image className="w-16 h-16 text-border" strokeWidth={1.5} />
                                  </span>
                                </div>
                                <p className="text-sm text-themeforeground">Landing Page Screenshot will appear here</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end gap-3">
                      <Button
                        type="button"
                        onClick={() => {
                          form.handleSubmit(handleSubmitUploadAdImage)()
                        }}
                        disabled={Boolean(
                          !form.watch('adImage') ||
                            !form.getValues('landingPageUrl') ||
                            isUploading ||
                            (landingPageImage && form.watch('landingPageUrl') === lastCapturedUrl),
                        )}
                      >
                        {isUploading ? 'Capturing...' : 'Capture'}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          form.handleSubmit(handleSubmitFunnelCongruenceAnalysis)()
                        }}
                        disabled={!adImageUrl || !landingPageImage || isUploading}
                      >
                        Next
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            ) : uploadStep === 2 && funnelCongruenceAnalysis ? (
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                  <div className="lg:col-span-2 space-y-6 rounded-lg p-6 border ">
                    <h3 className="font-semibold text-lg">Audience Details</h3>
                    <div className="space-y-3">
                      <h3 className="font-semibold text-sm">Ad Preview</h3>
                      <div>
                        <img
                          src={funnelCongruenceAnalysis.ad_images_data?.[0]}
                          alt=""
                          className="w-full h-[30svh] lg:h-[50svh] object-contain"
                        />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <h3 className="font-semibold text-sm">Landing Page Preview</h3>
                      <ScrollArea className="h-[30svh] lg:h-[50svh]">
                        <img
                          src={funnelCongruenceAnalysis.landing_page_image_data}
                          alt={form.getValues('landingPageUrl')}
                        />
                      </ScrollArea>
                    </div>
                  </div>
                  <div className="lg:col-span-3 rounded-lg border">
                    <FunnelCongruenceTabs funnelCongruenceAnalysis={funnelCongruenceAnalysis} width={width} />
                  </div>
                </div>
              </div>
            ) : null}
          </Form>
        </div>
      </Stack>
    </>
  )
}

const UploadAdImage = ({
  onFileSelect,
  previewUrl,
  disabled,
  onRemove,
}: {
  onFileSelect: (file: File | undefined) => void
  previewUrl: string | undefined
  disabled?: boolean
  onRemove: () => void
}) => {
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | undefined>(undefined)
  const [isPdf, setIsPdf] = useState<boolean>(false)

  const handleDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]
      if (file.type === 'application/pdf') {
        setIsPdf(true)
        setLocalPreviewUrl(URL.createObjectURL(file))
      } else {
        setIsPdf(false)
        const url = URL.createObjectURL(file)
        setLocalPreviewUrl(url)
      }
      onFileSelect(file)
    }
  }

  // Update isPdf state when previewUrl changes
  useEffect(() => {
    if (previewUrl) {
      const isPdfUrl = previewUrl.toLowerCase().endsWith('.pdf')
      setIsPdf(isPdfUrl)
    }
  }, [previewUrl])

  return (
    <Dropzone
      onDrop={handleDrop}
      accept={{
        'image/*': ['.png', '.jpg', '.jpeg'],
      }}
      disabled={disabled}
      noKeyboard={true}
    >
      {(dropzone: DropzoneState) => (
        <div className="h-[30svh] lg:h-[60svh] w-full bg-gray-50 flex flex-col justify-center items-center">
          {previewUrl || localPreviewUrl ? (
            <div className="w-full h-[30svh] lg:h-[60svh] relative">
              {isPdf ? (
                <div className="w-full h-full flex flex-col items-center justify-center p-4">
                  <object data={previewUrl || localPreviewUrl} type="application/pdf" className="w-full h-full">
                    <div className="w-full h-full flex flex-col items-center justify-center">
                      <div className="w-16 h-16 mb-4">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-full h-full text-themedestructive"
                        >
                          <path
                            d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M14 2V8H20"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16 13H8"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16 17H8"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10 9H9H8"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-themeforeground">PDF Document</p>
                      <p className="text-xs text-thememutedforeground mt-1">Unable to preview PDF</p>
                    </div>
                  </object>
                </div>
              ) : (
                <img
                  src={previewUrl || localPreviewUrl}
                  alt="Preview"
                  className="w-full h-[30svh] lg:h-[60svh] object-contain"
                />
              )}
              <div className="absolute bottom-0 rounded-b-lg bg-gradient-to-t from-black/50 to-transparent w-full h-20 flex justify-center items-center">
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute left-1/2 -translate-x-1/2 rounded-full bg-transparent border border-white text-white"
                  onClick={() => {
                    setLocalPreviewUrl(undefined)
                    setIsPdf(false)
                    onRemove()
                  }}
                >
                  Remove
                </Button>
              </div>
            </div>
          ) : dropzone.isDragAccept ? (
            <div className="text-sm font-medium">Drop your files here!</div>
          ) : (
            <div className="flex items-center flex-col text-sm font-medium text-thememutedforeground">
              <div className="w-[2.125rem] h-[2.125rem] rounded-full border-2 border-dashed flex justify-center items-center">
                <Upload />
              </div>
              <p className="">
                <span className="text-themedestructive">Upload files</span> or drag and drop
              </p>
              <p>PNG or JPG (max 10MB)</p>
            </div>
          )}
        </div>
      )}
    </Dropzone>
  )
}

export const ScoreMeasures = ({
  score,
  name,
  description,
}: {
  score: number
  name: string
  description: string
}) => {
  return (
    <div className="p-4 border rounded-xl space-y-3">
      <div className="flex justify-between gap-5">
        <div className="flex gap-3 items-center">
          <div className="w-8 h-8 rounded-full border flex justify-center items-center">
            {score >= 80 ? (
              <CheckCircle size={16} color="#28BE53BE" />
            ) : score > 40 ? (
              <InfoTriangle size={16} color="#FF8731" />
            ) : (
              <XCircle size={16} color="#FF0000" />
            )}
          </div>
          <div>
            <p className="text-sm font-semibold">
              {name
                .split('_')
                .join(' ')
                .split(' ')
                .map((word) => {
                  // Special case for 'cta' to make it uppercase
                  if (word.toLowerCase() === 'cta') {
                    return 'CTA'
                  }
                  return word.charAt(0).toUpperCase() + word.slice(1)
                })
                .join(' ')}
            </p>
          </div>
        </div>
        <div>
          <p
            className={`text-xs font-semibold ${score >= 80 ? 'text-[#28BE53BE]' : score > 40 ? 'text-[#FF8731]' : 'text-red-500'}`}
          >{`${score}/100`}</p>
        </div>
      </div>
      <div>
        <Progress value={score} color={score >= 80 ? 'bg-[#28BE53BE]' : score > 40 ? 'bg-[#FF8731]' : 'bg-red-500'} />
      </div>
      <div>
        <p className="text-sm font-medium text-thememutedforeground">{description}</p>
      </div>
    </div>
  )
}

export const FixesItem = ({
  heading,
  description,
  index,
}: {
  heading: string
  description: string
  index: number
}) => {
  return (
    <div className="p-4 border rounded-xl flex items-start gap-3">
      <div>
        <div className="w-8 h-8 text-sm font-bold rounded-full bg-green-50 flex justify-center items-center">
          <span>{index + 1}</span>
        </div>
      </div>
      <div>
        <p className="text-sm font-semibold">{heading}</p>
        <p className="text-sm text-thememutedforeground">{description}</p>
      </div>
    </div>
  )
}
